import { useState, useEffect } from "react";
import LOGO_URL from "../assets/deviDarshanLogo";

/**
 * Phase 36da.99.bp — SplashScreen
 *
 * Full-screen branded overlay shown on initial app load.  Lifecycle:
 *   • mounts visible
 *   • after `holdMs` (1500 by default) starts a `fadeMs` fade-out
 *   • after `holdMs + fadeMs` calls onFinished() so App can unmount it
 *
 * The component owns its own visibility timer.  Mounting it = showing it;
 * removing it = it's gone.  Parent only needs to handle the onFinished
 * callback to swap state ("showing splash" → "showing app").
 */
export default function SplashScreen({
    onFinished,
    holdMs = 1500,
    fadeMs = 400,
}) {
    const [fading, setFading] = useState(false);

    useEffect(() => {
        const startFade = setTimeout(() => setFading(true), holdMs);
        const unmount = setTimeout(() => onFinished?.(), holdMs + fadeMs);
        return () => { clearTimeout(startFade); clearTimeout(unmount); };
    }, [holdMs, fadeMs, onFinished]);

    return (
        <div style={{
            position: "fixed", inset: 0, zIndex: 99999,
            background: "linear-gradient(135deg, #FFFFFF 0%, #FAFAFA 100%)",
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            gap: 28, padding: 24,
            opacity: fading ? 0 : 1,
            transition: `opacity ${fadeMs}ms ease-out`,
            pointerEvents: fading ? "none" : "auto",
        }}>
            {/* Soft pulsing halo behind the logo */}
            <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{
                    position: "absolute", width: 460, height: 460, borderRadius: "50%",
                    background: "radial-gradient(closest-side, rgba(214,40,40,0.10), transparent)",
                    animation: "splash-pulse 2s ease-in-out infinite",
                }} />
                <img src={LOGO_URL} alt="Devi Darshan"
                    style={{
                        position: "relative",
                        maxWidth: "min(520px, 80vw)",
                        width: "100%", height: "auto",
                        animation: "splash-rise 700ms cubic-bezier(0.16, 1, 0.3, 1) both",
                    }} />
            </div>

            {/* System name */}
            <div style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                animation: "splash-rise 700ms 250ms cubic-bezier(0.16, 1, 0.3, 1) both",
            }}>
                <div style={{
                    fontSize: 22, fontWeight: 700, letterSpacing: "0.18em",
                    color: "#1F2937", textTransform: "uppercase",
                }}>
                    GarmentERP
                </div>
                <div style={{
                    fontSize: 11, fontWeight: 600, letterSpacing: "0.32em",
                    color: "#9CA3AF", textTransform: "uppercase",
                }}>
                    Production Management Suite
                </div>
            </div>

            {/* Progress dots */}
            <div style={{
                display: "flex", gap: 8, marginTop: 4,
                animation: "splash-rise 700ms 450ms cubic-bezier(0.16, 1, 0.3, 1) both",
            }}>
                {[0, 1, 2].map(i => (
                    <span key={i} style={{
                        width: 8, height: 8, borderRadius: "50%",
                        background: "#D62828",
                        animation: `splash-dot 1.2s ${i * 0.18}s ease-in-out infinite`,
                    }} />
                ))}
            </div>

            {/* SMIT credit — bottom-fixed */}
            <div style={{
                position: "absolute", bottom: 28, left: 0, right: 0,
                display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                animation: "splash-rise 700ms 650ms cubic-bezier(0.16, 1, 0.3, 1) both",
            }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", letterSpacing: "0.04em" }}>
                    Developed by
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1F2937", letterSpacing: "0.04em" }}>
                    Solapurmall Information Technology Private Limited
                </div>
            </div>

            {/* Keyframes — scoped inline so the component is fully self-contained
          and doesn't depend on the index.css file the user already maintains */}
            <style>{`
        @keyframes splash-pulse {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50%      { transform: scale(1.08); opacity: 1; }
        }
        @keyframes splash-rise {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes splash-dot {
          0%, 80%, 100% { transform: scale(0.8); opacity: 0.4; }
          40%           { transform: scale(1.0); opacity: 1.0; }
        }
      `}</style>
        </div>
    );
}